import {ceremonies} from "@/ceremonies";

const monthYearFormat = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'short',
    year: 'numeric'
});

const alphabeticalCollator = new Intl.Collator("en-US", {
    sensitivity: 'base',
    ignorePunctuation: true
});

export class Nomination {

    constructor(apiResponseObj) {
        // take all the apiResponse properties
        Object.assign(this, apiResponseObj);
    }

    get nominee_display_text() {
        return this.song_title ? `"${this.song_title}" - ${this.nominee}` : this.nominee;
    }

    static isActingCategory(_categoryName) {
        const normalizedCategoryName = _categoryName.toLowerCase();
        return ( normalizedCategoryName.startsWith('actor') || normalizedCategoryName.startsWith('actress') );
    }

    static isSongCategory(_categoryName) {
        return ( _categoryName.toLowerCase().includes('original song') );
    }

    static categoryDisplayNameHtml(_categoryName) {
        return _categoryName

            // display in all-caps
            .toUpperCase()

            .replace('OUTSTANDING PRODUCTION', 'OUTSTANDING<br>PRODUCTION')
            .replace('OUTSTANDING PICTURE', 'OUTSTANDING<br>PICTURE')
            .replace('UNIQUE AND ARTISTIC', 'UNIQUE & ARTISTIC')

            // add word-splitting suggestions
            .replace('CINEMATOGRAPHY', 'CINEMA&shy;TOGRAPHY')
            .replace('INTERNATIONAL', 'INTER&shy;NATIONAL')
            .replace('DOCUMENTARY', 'DOCU&shy;MENTARY')
            .replace('PRODUCTION', 'PRO&shy;DUCTION')
            .replace('HAIRSTYLING', 'HAIR&shy;STYLING')
            .replace('ANIMATED', 'ANI&shy;MATED')

            // shorten some of these older, long-winded category names
            .replace('(SCREENPLAY WRITTEN DIRECTLY FOR THE SCREEN)', '(ORIGINAL SCREENPLAY)')
            .replace('(STORY AND SCREENPLAY--WRITTEN DIRECTLY FOR THE SCREEN)', '(ORIGINAL SCREENPLAY)')
            .replace('(SCREENPLAY WRITTEN DIRECTLY FOR THE SCREEN--BASED ON FACTUAL MATERIAL OR ON STORY MATERIAL NOT PREVIOUSLY PUBLISHED OR PRODUCED)', '(ORIGINAL SCREENPLAY)')
            .replace('(STORY AND SCREENPLAY--BASED ON FACTUAL MATERIAL OR MATERIAL NOT PREVIOUSLY PUBLISHED OR PRODUCED)', '(ORIGINAL SCREENPLAY)')
            .replace('(STORY AND SCREENPLAY--BASED ON MATERIAL NOT PREVIOUSLY PUBLISHED OR PRODUCED)', '(ORIGINAL SCREENPLAY)')


            .replace('(SCREENPLAY BASED ON MATERIAL PREVIOUSLY PRODUCED OR PUBLISHED)', '(ADAPTED SCREENPLAY)')
            .replace('(SCREENPLAY--BASED ON MATERIAL FROM ANOTHER MEDIUM)', '(ADAPTED SCREENPLAY)')
            .replace('(SCREENPLAY BASED ON MATERIAL FROM ANOTHER MEDIUM)', '(ADAPTED SCREENPLAY)')
            .replace('(SCREENPLAY ADAPTED FROM OTHER MATERIAL)', '(ADAPTED SCREENPLAY)')

            .replace('(MUSIC SCORE OF A DRAMATIC OR COMEDY PICTURE)', '(SCORE)')
            .replace('(MUSIC SCORE OF A DRAMATIC PICTURE)', '(SCORE)')
            .replace('(SCORING OF A MUSICAL PICTURE)', '(MUSICAL SCORE)')

            .replace('(ORIGINAL SONG SCORE AND ITS ADAPTATION -OR- ADAPTATION SCORE)', '(ADAPTATION OR ORIGINAL SONG SCORE)')
            .replace('(ORIGINAL SONG SCORE AND ITS ADAPTATION OR ADAPTATION SCORE)', '(ADAPTATION OR ORIGINAL SONG SCORE)')
            .replace('(SCORING: ORIGINAL SONG SCORE AND ADAPTATION -OR- SCORING: ADAPTATION)', '(ADAPTATION OR ORIGINAL SONG SCORE)')
            .replace('(SCORING: ADAPTATION AND ORIGINAL SONG SCORE)', '(ADAPTATION OR ORIGINAL SONG SCORE)')
            .replace('(SCORE OF A MUSICAL PICTURE--ORIGINAL OR ADAPTATION)', '(ADAPTATION OR ORIGINAL SONG SCORE)')

            .replace('(SCORING OF MUSIC--ADAPTATION OR TREATMENT)', '(ADAPTED MUSIC SCORE)')

            .replace('(ORIGINAL SCORE--FOR A MOTION PICTURE [NOT A MUSICAL])', '(ORIGINAL MUSIC SCORE)')
            .replace('(MUSIC SCORE--SUBSTANTIALLY ORIGINAL)', '(ORIGINAL MUSIC SCORE)')

            .replace('(BLACK-AND-WHITE)', '(B&W)')
            .replace('SPECIAL ACHIEVEMENT AWARD', 'SPECIAL ACHIEVEMENT')

            //.replace('', '')
            ;
    }
}

export class FilmTitleInfo {
    constructor(apiResponseObj) {
        this._id = apiResponseObj._id;
        this.name = apiResponseObj.name;
        this.year = apiResponseObj.year;
        this.display_priority = apiResponseObj.display_priority;
        this.original_language = apiResponseObj.original_language;

        if ( apiResponseObj.nomCount ) {
            this.nomCount = apiResponseObj.nomCount;
            this.winCount = apiResponseObj.winCount;
            this.unofficialNomCount = apiResponseObj.unofficialNomCount;
        } else if ( apiResponseObj.nominations ) {
            this.nomCount = apiResponseObj.nominations.length;
            this.winCount = apiResponseObj.nominations.filter(n => n.won).length;
            this.unofficialNomCount = apiResponseObj.nominations.filter(n => n.unofficial).length;
        }
    }
    toString() {
        return `${this.name}: ${this.nominations.length} nominations`;
    }

    numNominations() {
        return this.nomCount;
    }
    numWins() {
        return this.winCount;
    }
    numUnofficialNominations() {
        return this.unofficialNomCount;
    }
}

export class Film {
    constructor(apiResponseObj) {

        // for the most part, take all the apiResponse properties
        Object.assign(this, apiResponseObj);

        // some should be converted to proper types
        this.release_date = new Date(this.release_date);
        this.runtime = Number(this.runtime);
        this.nominations = this.nominations.map(n => new Nomination(n));

        this.streamingProviders = apiResponseObj.streamingProviders || [];
    }
    toString() {
        return `${this.name} (${this.year}): ${this.nominations.length} nominations`;
    }

    genreText(_seperatingCharacter) {

        if ( !this.genres?.length ) {
            return "";
        }

        let text = '';
        if ( !_seperatingCharacter ) {
            _seperatingCharacter = ' ';
        }

        this.genres.forEach((genre, index, array) => {
            text += genre;
            if ( index !== (array.length - 1) ) {
                text += _seperatingCharacter;
            }
        });
        return text;
    }

    runtimeText() {

        if ( !this.runtime ) {
            return '';
        }

        const hours = Math.floor(this.runtime / 60);
        const minutes = this.runtime % 60;
        return ( (hours > 0)? `${hours}h ${minutes}m` : `${minutes}m` );
    }

    numNominations() {
        return this.nominations.length;
    }
    numWins() {
        return this.wins().length;
    }
    numUnofficialNominations() {
        return this.unofficialNominations().length;
    }

    wins() {
        return this.nominations.filter(n => n.won);
    }

    unofficialNominations() {
        return this.nominations.filter(n => n.unofficial);
    }

    doStreamingProvidersRequireMembership() {
        return !!this.streamingProviders?.membership_required;
    }

    getSubscriptionProviders() {
        return this.streamingProviders?.flatrate || [];
    }

    getRentalAndPurchaseProviders() {

        // add all the rental providers to our result array
        const result = [...this.streamingProviders?.rent || []];

        // add any purchase providers that aren't already in our results
        if ( this.streamingProviders?.buy ) {
            this.streamingProviders.buy.forEach(buyProv => {
                if ( !result.some(resultProv => resultProv.id === buyProv.id) ) {
                    result.push(buyProv);
                }
            })
        }

        return result;
    }

    getFreeAndAdsProviders() {

        // add all the free providers to our result array
        const result = [...this.streamingProviders?.free || []];

        // add any ad-only providers that aren't already in our results
        if ( this.streamingProviders?.ads ) {
            this.streamingProviders.ads.forEach(adsProv => {
                if ( !result.some(resultProv => resultProv.id === adsProv.id) ) {
                    result.push(adsProv);
                }
            })
        }
        return result;
    }

    imdbLink() {
        if (!this.imdb?.id) {
            const urlSafeMovieName = encodeURI(`"${this.name.replace(' ', '+')}"`);
            let url = `https://www.imdb.com/search/title/?title=${urlSafeMovieName}`;

            if ( this.year ) {
                url += `&release_date=${this.year},${this.year + 1}`;
            }
            return url;
        }
        return `https://www.imdb.com/title/${this.imdb.id}`;
    }

    nominationsForCategory(_category) {
        return this.nominations.filter(nom => nom.category.name == _category);

        // const noms = this.nominations.filter(nom => nom.category.name == _category);
        //
        // return noms.sort((nom1, nom2) => {
        //     if (nom1.won && !nom2.won) {
        //         return -1;
        //     }
        //     if (nom2.won && !nom1.won) {
        //         return 1;
        //     }
        //     if (nom1.unofficial && !nom2.unofficial) {
        //         return 1;
        //     }
        //     if (nom2.unofficial && !nom1.unofficial) {
        //         return -1;
        //     }
        //     return 0;
        // });
    }

    static sortFilmsAlphabetically(filmlist) {

        const compareFilmNamesAlphabetically = ((film1, film2) => {
            // normalize the titles by stripping leading "the", "a" or "an"
            const normalizedTitle1 = film1.name.replace(/^the\s+/i, '').replace(/^a\s+/i, '').replace(/^an\s+/i, '');
            const normalizedTitle2 = film2.name.replace(/^the\s+/i, '').replace(/^a\s+/i, '').replace(/^an\s+/i, '');
            return alphabeticalCollator.compare(normalizedTitle1, normalizedTitle2);
        });

        filmlist.sort(compareFilmNamesAlphabetically);
    }

    static sortFilmsByPrioirity(filmlist) {

        filmlist.sort((film1, film2) => {

            // Instead of simply comparing two films' display_priorities, this looks through the nominations lists of
            //  each film, to more reasonably sort ties (like, two non-winning best picture nominees have the same
            //  display_priority; but the one that won best actor should definitely be treated as higher priority).
            for ( let i = 0; i < Math.min(film1.nominations.length, film2.nominations.length); i++ ) {
                if ( film1.nominations[i].display_priority < film2.nominations[i].display_priority ) {
                    return 1;
                } else if ( film1.nominations[i].display_priority > film2.nominations[i].display_priority ) {
                    return -1;
                }
            }
            return (film2.nominations.length - film1.nominations.length)

            //return (film2.display_priority - film1.display_priority);
        });

    }

    static sortFilmsChronologically(filmlist) {
        filmlist.sort((film1, film2) => {
            return (film1.year - film2.year);
        });
    }

    static getNominatedCategoriesForFilms(_filmlist) {

        const categories = [];
        _filmlist.forEach((film) => {
            film.nominations.forEach((nom) => {
                const foundCategory = categories.find(cat => {
                    return (nom.category.name === cat.name);
                });
                if ( !foundCategory ) {
                    categories.push(nom.category);
                }
            })
        });

        // sort by priority
        categories.sort((cat1, cat2) => {
           return cat2.display_priority - cat1.display_priority;
        });

        return categories;
    }

    static filterFilmsNominatedForCategory(_filmlist, _category, _ceremony) {

        return _filmlist.filter((film) => {
            return (film.nominations.filter(nom => (nom.category.name == _category) && (nom.ceremony === _ceremony)).length > 0);
        });
    }

    static getNominationsForCategory(_filmList, _categoryName) {

        // get the nominations for this film, in this category, and tag on the film id and name.
        const nominations = _filmList.reduce((accNomList, curFilm) => {
            const filmNoms = curFilm.nominations.filter(nom => nom.category.name == _categoryName).map(nom => {
                return {
                    ...nom,
                    film_name: curFilm.name,
                    film_id: curFilm._id
                }
            });
            return [ ...accNomList, ...filmNoms ]
        }, []);


        const compareNominationsAlphabeticallyByFilmName = ((nomination1, nomination2) => {
            // normalize the titles by stripping leading "the", "a" or "an"
            const normalizedTitle1 = nomination1.film_name.replace(/^the\s+/i, '').replace(/^a\s+/i, '').replace(/^an\s+/i, '');
            const normalizedTitle2 = nomination2.film_name.replace(/^the\s+/i, '').replace(/^a\s+/i, '').replace(/^an\s+/i, '');
            return alphabeticalCollator.compare(normalizedTitle1, normalizedTitle2);
        });

        const compareNominationsAlphabeticallyByNomineeLastName = ((nomination1, nomination2) => {
            const lastName1 = nomination1.nominee.split(" ").pop();
            const lastName2 = nomination2.nominee.split(" ").pop();
            return alphabeticalCollator.compare(lastName1, lastName2);
        });

        const compareNominationsAlphabeticallyByNomineeTitle = ((nomination1, nomination2) => {
            // normalize the titles by stripping leading "the", "a" or "an"
            const normalizedTitle1 = nomination1.nominee.replace(/^the\s+/i, '').replace(/^a\s+/i, '').replace(/^an\s+/i, '');
            const normalizedTitle2 = nomination2.nominee.replace(/^the\s+/i, '').replace(/^a\s+/i, '').replace(/^an\s+/i, '');
            return alphabeticalCollator.compare(normalizedTitle1, normalizedTitle2);
        });

        // sort the nominations -- how we do this depends on the category
        if ( Nomination.isActingCategory(_categoryName) ) {
            nominations.sort(compareNominationsAlphabeticallyByNomineeLastName);
        } else if ( Nomination.isSongCategory(_categoryName) ) {
            nominations.sort(compareNominationsAlphabeticallyByNomineeTitle);
        } else {
            nominations.sort(compareNominationsAlphabeticallyByFilmName);
        }

        return nominations;
    }

    static getCeremonyHeaderText(_ceremony) {
        const ceremony_num = Number(_ceremony);
        if ( ceremony_num <= 0 ) {
            return '';
        }
        return `${Film.getCeremonyOrdinalText(ceremony_num)} (${Film.getCeremonyYearText(ceremony_num)})`
    }

    static getCeremonyOrdinalText(_ceremony) {
        const ceremony_num = Number(_ceremony);
        if ( ceremony_num <= 0 ) {
            return '';
        }
        return `${ceremony_num}${
            (ceremony_num % 10 == 1 && ceremony_num % 100 != 11) ? 'st' :
                (ceremony_num % 10 == 2 && ceremony_num % 100 != 12) ? 'nd' :
                    (ceremony_num % 10 == 3 && ceremony_num % 100 != 13) ? 'rd' :
                        'th'}`;
    }
    static getCeremonyYearText(_ceremony) {
        const date = this.getDateOfCeremony(_ceremony);
        const year = date.getUTCFullYear();

        // There were two ceremonies in 1930, so add the month for this year
        return ( year === 1930 ) ? monthYearFormat.format(date) : year.toString();
    }

    static ceremonyYearsHonoredText(_ceremony) {
        const honoring_years = ceremonies.find((obj) => {
            return ( obj.ceremony === Number(_ceremony) );
        }).honoring_years;

        return honoring_years.join('/');
    }

    static getDateOfCeremony(_ceremony) {

        const dateOfGivenCeremony = ceremonies.find((obj) => {
            return ( obj.ceremony === Number(_ceremony) );
        });

        return new Date(dateOfGivenCeremony?.date);
    }

    static categoryDisplayNameHtml(_categoryName) {
        return Nomination.categoryDisplayNameHtml(_categoryName);
    }

    static getCategoryAchievementImage(_normalizedCategoryName) {

        switch ( _normalizedCategoryName?.toUpperCase() ) {
            case "BEST PICTURE":
                return 'achievements/film-reel.svg';

            case "ACTOR IN A LEADING ROLE":
            case "ACTRESS IN A LEADING ROLE":
            case "ACTOR IN A SUPPORTING ROLE":
            case "ACTRESS IN A SUPPORTING ROLE":
                return 'achievements/comedy-tragedy.svg';

            case "CASTING":
                return 'achievements/bullhorn.svg';

            case "DIRECTING":
                return 'achievements/clapper-board.svg';

            case "WRITING (ORIGINAL SCREENPLAY)":
            case "WRITING (ADAPTED SCREENPLAY)":
                return 'achievements/typewriter.svg';

            case "CINEMATOGRAPHY":
                return 'achievements/film-camera.svg';

            case "VISUAL EFFECTS":
                return 'achievements/explosion.svg';

            case "PRODUCTION DESIGN":
                return 'achievements/paintbrush-and-drafting-triangle.svg';

            case "FILM EDITING":
                return 'achievements/film-and-scissors.svg';

            case "SOUND":
                return 'achievements/boom-mic.svg';

            case "MUSIC (ORIGINAL SCORE)":
                return 'achievements/sheet-music.svg';

            case "MUSIC (ORIGINAL SONG)":
                return 'achievements/headphones.svg';

            case "INTERNATIONAL FEATURE FILM":
                return 'achievements/globe.svg';

            case "MAKEUP AND HAIRSTYLING":
                return 'achievements/dressing-room-vanity.svg';

            case "COSTUME DESIGN":
                return 'achievements/dress.svg';

            case "ANIMATED FEATURE FILM":
                return 'achievements/animation-desk.svg';

            case "ANIMATED SHORT FILM":
                return 'achievements/animation-desk-with-timer.svg';

            case "DOCUMENTARY FEATURE FILM":
                return 'achievements/handheld-camera.svg';

            case "DOCUMENTARY SHORT FILM":
                return 'achievements/handheld-camera-with-timer.svg';

            case "LIVE ACTION SHORT FILM":
                return 'achievements/film-reel-with-timer.svg';

            case "HONORARY AWARD":
                return 'achievements/ribbon.svg';
        }
        return 'achievements/checkmark.svg';
    }

}